import {
  Analytics,
  Control,
  Functionality,
  Grouping,
  Language,
  View,
} from '@yleisradio/areena-types';
import {
  AdobeAppLabels,
  Comscore,
  Device,
  LoginSubRoute,
  UtmParameters,
} from '../appTypings';
import { HbbTVDevice } from '../deviceTypes';
import { ApplicationResolution } from '../applicationResolution';
import { cookieValue } from '../utils/cookie';
import { LoggedInUser, Mode, tizenMode } from '../../common/types';
import { applicationType } from '../../common/utils';
import {
  CREDENTIALS_LOGIN_SUB_PATH,
  PAIR_LOGIN_SUB_PATH,
  REGISTER_LOGIN_SUB_PATH,
} from '../Routes/routeNames';
import { Features } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { tabSlugToTabIndex } from '../utils/view';
import { v4 as uuid } from 'uuid';

export const addUserId = (comscore: Comscore, userId: string | undefined) => {
  return {
    ...comscore,
    ...(userId && { yle_sk: userId }),
  };
};

const getDeviceVendor = (device: Device | undefined) => {
  return device?.vendor ? device?.vendor : '';
};

const getPlatform = () => 'big screen';

const getDeviceFamily = (device: Device | undefined) => {
  return device?.family || '';
};

export const getVrsDevice = (device: Device | undefined) => {
  const vendor = getDeviceVendor(device);

  if (device instanceof HbbTVDevice) {
    return `smart tv:${vendor} hbbtv`;
  }

  const family = getDeviceFamily(device);

  return `smart tv:${vendor} ${family}`;
};

export const getAppLabelsFromFeatures = (
  appFeatures: Pick<Features, 'version' | 'applicationResolution' | 'device'>
): AdobeAppLabels => {
  const { version, applicationResolution, device } = appFeatures;

  const vrsDevice = getVrsDevice(device);
  const vrsProduct = applicationType;
  const vrsPlatform = getPlatform();

  const areenaAbCookie = cookieValue('areena_ab');
  const yleSelvaCookie = cookieValue('yle_selva');
  const yleRecCookie = cookieValue('yle_rec');
  const userConsentCookie = cookieValue('userconsent');
  const areena_ab = areenaAbCookie || '';
  const yle_selva = yleSelvaCookie || '';
  const yle_rec = yleRecCookie || '';
  const userconsent = userConsentCookie || '';

  return {
    'yle.vrsDevice': vrsDevice,
    'yle.vrsProduct': vrsProduct,
    'yle.vrsPlatform': vrsPlatform,
    Resolution:
      applicationResolution === ApplicationResolution.FULLHD
        ? '1920x1080'
        : '1280x720',
    DeviceName: device ? `${device?.vendor} ${device?.model}` : 'unknown',
    OSVersion: device?.version || 'unknown',
    yle_client: `leanback-areena.${version}`,
    yle_ab_string: decodeURIComponent(areena_ab),
    yle_selva: decodeURIComponent(yle_selva),
    yle_rec: decodeURIComponent(yle_rec),
    userconsent: decodeURIComponent(userconsent),
  };
};

export const findControlAnalyticsByFunctionality = (
  functionality: Functionality,
  groupings: Grouping[] | undefined
): Analytics | undefined => {
  if (groupings) {
    const predicate = (control: Control) =>
      control.functionality?.id === functionality.id;

    return groupings
      .find(grouping => grouping?.controls.find(predicate))
      ?.controls.find(predicate)?.analytics;
  }
};

export const getPreviewUtmParameters = (
  mode: Mode,
  id: string,
  isPlayerUri: boolean
): string => {
  const uriPostfix = isPlayerUri ? 'startover' : 'preview';
  const utmParameters = [
    `utm_source=${mode === tizenMode ? 'samsung' : mode}`,
    `utm_medium=external-tv`,
    `utm_campaign=${encodeURIComponent(id)}`,
    `utm_content=${encodeURIComponent(id)}-${uriPostfix}`,
  ];
  return utmParameters.join('&');
};

export const analyticsPageNames = {
  profile: 'profile_select',
  profile_edit: 'profile_edit',
  edit_profile_name: 'profile_name',
  edit_profile_image: 'profile_image',
  add_user: 'add_user',
  add_sub_profile: 'add_children_profile',
  add_sub_profile_info: 'add_children_profile_info',
  search: 'search',
  settings: 'settings',
  consent: 'consent',
  consent_edit: 'consent_edit',
  tos: 'tos_changed',
  favorites: 'favorites',
  onboarding: 'onboarding_login',
};

export const getFunctionalityAnalytics = (
  functionality: string,
  language: Language
): Analytics => ({
  pageload: {
    comscore: {
      countername: functionality,
      yle_ui_language: language,
    },
  },
  context: {
    comscore: {
      yle_referer: functionality,
    },
  },
});

type LoginAnalyticsRouteMap = {
  [key in LoginSubRoute]: string;
};

export const loginAnalyticsRouteMap: LoginAnalyticsRouteMap = {
  [PAIR_LOGIN_SUB_PATH]: 'add_user_login_phone',
  [CREDENTIALS_LOGIN_SUB_PATH]: 'add_user_login_remote',
  [REGISTER_LOGIN_SUB_PATH]: 'add_user_create_account',
};

export const onboardingAnalyticsRouteMap: LoginAnalyticsRouteMap = {
  [PAIR_LOGIN_SUB_PATH]: 'onboarding_login_phone',
  [CREDENTIALS_LOGIN_SUB_PATH]: 'onboarding_login_remote',
  [REGISTER_LOGIN_SUB_PATH]: 'onboarding_create_account',
};

/**
 * Always send parent ID when user is subaccount
 * @param user
 * @returns {undefined | string}
 */
export const getUserIdForYleSk = (user: LoggedInUser | undefined | null) => {
  if (!user) return undefined;
  return user.isSubAccount && user.parentId ? user.parentId : user.userId;
};

export function getUtmParameters(
  mode: Mode,
  isPlayer: boolean,
  id: string
): UtmParameters {
  return {
    utm_source: mode === tizenMode ? 'samsung' : mode,
    utm_campaign: encodeURIComponent(id),
    utm_content: encodeURIComponent(id).concat(
      '-',
      isPlayer ? 'startover' : 'preview'
    ),
    utm_medium: 'external-tv',
  };
}

export function getTabAnalyticsFromView(
  view: View,
  activeTabName?: string | null
): Analytics {
  const activeTabIndex = activeTabName
    ? (tabSlugToTabIndex(activeTabName, view.tabs) ?? 0)
    : 0;

  return {
    ...view.tabs?.[activeTabIndex]?.analytics,
  };
}

export const generatePageloadId = () => uuid();

export function getAnalyticsWithPageloadId(
  analytics: Analytics,
  pageloadId: string
): Analytics {
  return {
    ...analytics,
    app: {
      analytics: {
        yle_pageloadid: pageloadId,
      },
    },
  };
}
